import React, { useState, useRef, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { PBPUTILS } from "../../config/API";
import jsPDF from 'jspdf';
import SuccessPage from '../SuccessPage';

const DistributorReportForm = () => {
  // Get current date and calculate last month
  const getDefaultDate = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    let lastMonth = currentMonth - 1;
    let year = currentYear;

    if (lastMonth < 0) {
      lastMonth = 11;
      year = currentYear - 1;
    }

    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    return {
      month: monthNames[lastMonth],
      year: year.toString()
    };
  };

  const defaultDate = getDefaultDate();

  const [rows, setRows] = useState([
    {
      id: 1,
      packsSold: '',
      manufacturerName: '',
      manufacturerAddress: '',
      firstImporter: '',
      distributorName: '',
      distributorAddress: '',
      brandFamily: '',
    }
  ]);

  const [submitting, setSubmitting] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [submissionData, setSubmissionData] = useState(null);
  const [signatureDataURL, setSignatureDataURL] = useState('');
  const [printedName, setPrintedName] = useState('');
  const [title, setTitle] = useState('');

  // Get taxpayer ID and business name from localStorage
  const [taxpayerId, setTaxpayerId] = useState('');
  const [businessName, setBusinessName] = useState('');

  // Distributor Information State
  const [distributorInfo, setDistributorInfo] = useState({
    distributor: '',
    month: defaultDate.month,
    year: defaultDate.year,
    expirationDate: ''
  });

  // NPM Brands State
  const [npmBrands, setNpmBrands] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);

  // Distributors State
  const [distributors, setDistributors] = useState([]);
  const [loadingDistributors, setLoadingDistributors] = useState(false);

  // Month options for dropdown
  const monthOptions = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Year options
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => (currentYear - 3 + i).toString());

  // Signature pad state and ref
  const sigCanvas = useRef(null);
  const [isSigned, setIsSigned] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [currentDate] = useState(new Date().toLocaleDateString());

  // Get taxpayer ID and business name from localStorage on component mount
  useEffect(() => {
    const storedTaxpayerId = localStorage.getItem("selectedTaxPayer");
    const storedBusinessName = localStorage.getItem("selectedBusinessName");
    const storedExpirationDate = localStorage.getItem("businessLicenseExpirationDate");

    if (storedTaxpayerId) {
      setTaxpayerId(storedTaxpayerId);
    } else {
      setTaxpayerId('109');
    }

    if (storedBusinessName) {
      setBusinessName(storedBusinessName);
      setDistributorInfo(prev => ({
        ...prev,
        distributor: storedBusinessName
      }));
    } else {
      setDistributorInfo(prev => ({
        ...prev,
        distributor: 'Black Canyon Distribution, LLC - PBPN License #41112-7'
      }));
    }

    if (storedExpirationDate) {
      setDistributorInfo(prev => ({
        ...prev,
        expirationDate: storedExpirationDate
      }));
    }
  }, []);

  // Fetch NPM brands and distributors when taxpayerId is available
  useEffect(() => {
    if (taxpayerId) {
      fetchNpmBrands();
      fetchDistributors();
    }
  }, [taxpayerId]);

  // Initialize canvas size after component mounts
  useEffect(() => {
    const updateCanvasSize = () => {
      const container = document.getElementById('signature-container');
      if (container) {
        const width = container.clientWidth - 32;
        const height = 200;
        setCanvasSize({ width, height });
      }
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);

    return () => window.removeEventListener('resize', updateCanvasSize);
  }, []);

  // Fetch NPM brands from API
  const fetchNpmBrands = async () => {
    try {
      setLoadingBrands(true);
      const response = await fetch(`${PBPUTILS}?taxpayer=${taxpayerId}&action=getNPMs`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(data)

      if (data.status === 'success') {
        setNpmBrands(data.data || []);
      } else {
        console.error('Failed to fetch NPM brands:', data.message);
        setNpmBrands([]);
      }
    } catch (error) {
      console.error('Error fetching NPM brands:', error);
      setNpmBrands([]);
    } finally {
      setLoadingBrands(false);
    }
  };

  // Fetch distributors from API
  const fetchDistributors = async () => {
    try {
      setLoadingDistributors(true);
      const response = await fetch(`${PBPUTILS}?taxpayer=${taxpayerId}&action=getdistributors`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'success') {
        const distributorsData = data.data || [];
        setDistributors(distributorsData);

        // Set default distributor from localStorage after fetching distributors
        const storedTaxpayerId = localStorage.getItem("selectedTaxPayer");
        if (storedTaxpayerId && distributorsData.length > 0) {
          // Find the distributor that matches the stored taxpayer ID
          const defaultDistributor = distributorsData.find(dist => dist.sval.toString() === storedTaxpayerId);
          if (defaultDistributor) {
            // Auto-select this distributor in the first row
            setRows(prevRows =>
              prevRows.map((row, index) =>
                index === 0 ? {
                  ...row,
                  distributorName: defaultDistributor.dval,
                  distributorAddress: defaultDistributor.address1 || ''
                } : row
              )
            );
          }
        }
      } else {
        console.error('Failed to fetch distributors:', data.message);
        setDistributors([]);
      }
    } catch (error) {
      console.error('Error fetching distributors:', error);
      setDistributors([]);
    } finally {
      setLoadingDistributors(false);
    }
  };

  const handleInputChange = (e, rowId = null) => {
    const { name, value } = e.target;

    if (rowId) {
      const nextValue =
        e.target.type === 'number' && value !== ''
          ? String(Math.max(0, Number(value)))
          : value;
      setRows(prevRows =>
        prevRows.map(row =>
          row.id === rowId ? { ...row, [name]: nextValue } : row
        )
      );
    }
  };

  // Handle manufacturer selection and auto-fill manufacturer info
  const handleManufacturerChange = (rowId, selectedValue) => {
    if (!selectedValue) {
      // Clear manufacturer info if no manufacturer is selected
      setRows(prevRows =>
        prevRows.map(row =>
          row.id === rowId ? {
            ...row,
            manufacturerName: '',
            manufacturerAddress: ''
          } : row
        )
      );
      return;
    }

    // Find the selected manufacturer from npmBrands
    const selectedManufacturer = npmBrands.find(brand => brand.sval.toString() === selectedValue);

    if (selectedManufacturer) {
      setRows(prevRows =>
        prevRows.map(row =>
          row.id === rowId ? {
            ...row,
            manufacturerName: selectedManufacturer.dval,
            manufacturerAddress: selectedManufacturer.address1 || ''
          } : row
        )
      );
    }
  };

  // Handle distributor selection and auto-fill distributor info
  const handleDistributorChange = (rowId, selectedValue) => {
    if (!selectedValue) {
      // Clear distributor info if no distributor is selected
      setRows(prevRows =>
        prevRows.map(row =>
          row.id === rowId ? {
            ...row,
            distributorName: '',
            distributorAddress: ''
          } : row
        )
      );
      return;
    }

    // Find the selected distributor from distributors
    const selectedDistributor = distributors.find(dist => dist.sval.toString() === selectedValue);

    if (selectedDistributor) {
      setRows(prevRows =>
        prevRows.map(row =>
          row.id === rowId ? {
            ...row,
            distributorName: selectedDistributor.dval,
            distributorAddress: selectedDistributor.address1 || ''
          } : row
        )
      );
    }
  };

  // Handle distributor information changes
  const handleDistributorInfoChange = (field, value) => {
    setDistributorInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addRow = () => {
    const newRow = {
      id: Date.now(),
      packsSold: '',
      manufacturerName: '',
      manufacturerAddress: '',
      firstImporter: '',
      distributorName: '',
      distributorAddress: '',
      brandFamily: '',
    };
    setRows(prevRows => [...prevRows, newRow]);
  };

  const removeRow = (rowId) => {
    if (rows.length > 1) {
      setRows(prevRows => prevRows.filter(row => row.id !== rowId));
    }
  };

  const calculateTotal = () => {
    return rows.reduce((total, row) => {
      const packs = parseInt(row.packsSold) || 0;
      return total + packs;
    }, 0);
  };

  // Helper function to convert base64 data URL to File object
  const dataURLtoFile = (dataurl, filename) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  };

  // Add this helper function to convert PDF to Blob
  const pdfToBlob = (pdf) => {
    const pdfOutput = pdf.output('blob');
    return pdfOutput;
  };

  // Generate PDF function with signature and proper labels - accepts optional signature override (use when generating for send, since setState is async)
  const generatePDF = (signatureDataURLOverride) => {
    const pdf = new jsPDF();
    const signatureToUse = signatureDataURLOverride !== undefined ? signatureDataURLOverride : signatureDataURL;

    const formatAmount = (value) => {
      if (value === null || value === undefined) return '0';
      const s = String(value).replace(/,/g, '').trim();
      if (s === '') return '0';
      const num = Number(s);
      if (isNaN(num)) return String(value);
      if (s.includes('.')) {
        const decimals = s.split('.')[1].length;
        return num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
      }
      return num.toLocaleString();
    };

    // Set font and colors
    pdf.setFont('helvetica');
    pdf.setTextColor(0, 0, 0);

    // Header Section
    pdf.setFontSize(16);
    pdf.text('Monthly Distributor Report', 105, 20, { align: 'center' });

    pdf.setFontSize(12);
    pdf.text('Prairie Band Potawatomi Tax Commission', 105, 30, { align: 'center' });
    pdf.text('16281 Q Road', 105, 36, { align: 'center' });
    pdf.text('Mayetta, KS 66509', 105, 42, { align: 'center' });

    pdf.setFontSize(10);
    pdf.text('Reports and Payment due on the 10th of each Month', 105, 50, { align: 'center' });

    // Distributor Information
    pdf.setFontSize(12);
    pdf.setTextColor(0, 0, 0);
    pdf.text('Distributor Information:', 20, 65);
    pdf.setFontSize(10);
    pdf.text(`Distributor: ${distributorInfo.distributor}`, 20, 72);
    pdf.text(`Month: ${distributorInfo.month} ${distributorInfo.year}`, 20, 78);
    pdf.text(`PBPN Tribal Distributor License Expiration Date: ${distributorInfo.expirationDate}`, 20, 84);

    if (taxpayerId) {
      pdf.text(`Taxpayer ID: ${taxpayerId}`, 20, 90);
    }

    // Report Title
    pdf.setFontSize(12);
    pdf.setTextColor(0, 0, 0);
    pdf.text('Sales of Non-Participating Manufacturer (NPM) Cigarettes to PBPN Retailers', 20, 110);

    // Sales Data Table
    let yPosition = 120;

    // Table Header - Fixed column positions
    pdf.setFontSize(9); // Smaller font for header to fit better
    pdf.setTextColor(0, 0, 0);
    pdf.setFillColor(240, 240, 240);
    pdf.rect(20, yPosition, 180, 8, 'F');

    // Column headers with proper spacing
    pdf.text('Brand Family Name', 22, yPosition + 6);
    pdf.text('# of Packs', 60, yPosition + 6);
    pdf.text('Manufacturer', 80, yPosition + 6);
    pdf.text('Distributor', 120, yPosition + 6);
    pdf.text('First Importer', 160, yPosition + 6);

    yPosition += 12;

    // Sales Data Rows
    rows.forEach((row, index) => {
      // Add top padding to each row
      yPosition += 2;

      if (yPosition > 270) {
        pdf.addPage();
        yPosition = 20;
        // Add header again on new page
        pdf.setFillColor(240, 240, 240);
        pdf.rect(20, yPosition, 180, 8, 'F');
        pdf.text('Brand Family Name', 22, yPosition + 6);
        pdf.text('# of Packs', 60, yPosition + 6);
        pdf.text('Manufacturer', 80, yPosition + 6);
        pdf.text('Distributor', 120, yPosition + 6);
        pdf.text('First Importer', 160, yPosition + 6);
        yPosition += 12;
        yPosition += 2;
      }

      // Start new page if we're running out of space
      if (yPosition > 250) {
        pdf.addPage();
        yPosition = 20;
        yPosition += 2;
      }

      // Row number
      pdf.setFontSize(9);
      pdf.text(`${index + 1}.`, 20, yPosition);

      // Brand Family - multiline with proper wrapping
      const brandFamily = row.brandFamily || 'Not provided';
      // Brand Family column width is from x=25 to x=60, so max width is ~30 units
      const brandFamilyLines = pdf.splitTextToSize(brandFamily, 30);
      brandFamilyLines.forEach((line, lineIndex) => {
        pdf.text(line, 25, yPosition + (lineIndex * 4));
      });
      const brandFamilyHeight = brandFamilyLines.length * 4;

      // Packs Sold - centered under header (numbers don't need wrapping)
      const packsSold = row.packsSold || '0';
      pdf.text(formatAmount(packsSold), 63, yPosition, { align: 'center' });

      // Manufacturer info - multiline with proper wrapping
      const manufacturerName = row.manufacturerName || 'Not provided';
      // Manufacturer column width is from x=80 to x=120, so max width is ~35-38 units
      const manufacturerLines = pdf.splitTextToSize(manufacturerName, 35);
      manufacturerLines.forEach((line, lineIndex) => {
        pdf.text(line, 80, yPosition + (lineIndex * 4));
      });
      const manufacturerNameHeight = manufacturerLines.length * 4;
      
      // Calculate manufacturer address height
      let manufacturerAddressHeight = 0;
      if (row.manufacturerAddress) {
        const addressLines = pdf.splitTextToSize(`${row.manufacturerAddress}`, 35);
        manufacturerAddressHeight = addressLines.length * 4;
      }
      const manufacturerTotalHeight = manufacturerNameHeight + manufacturerAddressHeight;

      // Distributor - multiline with proper wrapping
      const distributorName = row.distributorName || 'Not provided';
      // Distributor column width is from x=120 to x=160, so max width is ~35 units
      const distributorLines = pdf.splitTextToSize(distributorName, 35);
      distributorLines.forEach((line, lineIndex) => {
        pdf.text(line, 120, yPosition + (lineIndex * 4));
      });
      const distributorNameHeight = distributorLines.length * 4;
      
      // Calculate distributor address height
      let distributorAddressHeight = 0;
      if (row.distributorAddress) {
        const distAddressLines = pdf.splitTextToSize(`${row.distributorAddress}`, 35);
        distributorAddressHeight = distAddressLines.length * 4;
      }
      const distributorTotalHeight = distributorNameHeight + distributorAddressHeight;

      // First Importer - multiline with proper wrapping
      const importerText = row.firstImporter || 'Not provided';
      // First Importer column width is from x=160 to x=200, so max width is ~35 units
      const importerLines = pdf.splitTextToSize(importerText, 35);
      importerLines.forEach((line, lineIndex) => {
        pdf.text(line, 160, yPosition + (lineIndex * 4));
      });
      const importerHeight = importerLines.length * 4;

      // Render Manufacturer Address right after manufacturer name
      if (row.manufacturerAddress) {
        const addressLines = pdf.splitTextToSize(`${row.manufacturerAddress}`, 35);
        addressLines.forEach((line, lineIndex) => {
          if (yPosition + manufacturerNameHeight + (lineIndex * 4) > 270) {
            pdf.addPage();
            yPosition = 20;
            yPosition += 2;
          }
          pdf.text(line, 80, yPosition + manufacturerNameHeight + (lineIndex * 4));
        });
      }

      // Render Distributor Address right after distributor name
      if (row.distributorAddress) {
        const distAddressLines = pdf.splitTextToSize(`${row.distributorAddress}`, 35);
        distAddressLines.forEach((line, lineIndex) => {
          if (yPosition + distributorNameHeight + (lineIndex * 4) > 270) {
            pdf.addPage();
            yPosition = 20;
            yPosition += 2;
          }
          pdf.text(line, 120, yPosition + distributorNameHeight + (lineIndex * 4));
        });
      }

      // Update yPosition to account for the tallest column in this row (including addresses)
      const maxColumnHeight = Math.max(6, brandFamilyHeight, manufacturerTotalHeight, distributorTotalHeight, importerHeight);
      yPosition += maxColumnHeight;

      // First Importer Address if available (if it's separate from firstImporter field)
      // Note: firstImporter is already handled above, this section is for additional address info if needed
      // Keeping this section for consistency but it may not be used if firstImporter already contains the full address

      yPosition += 4;

      // Add a separator line between rows if there's space
      if (yPosition < 270) {
        pdf.setDrawColor(200, 200, 200);
        pdf.line(20, yPosition, 190, yPosition);
        yPosition += 2;
      }
    });

    // Total - INCREASED TOP PADDING
    if (yPosition > 250) {
      pdf.addPage();
      yPosition = 20;
    }

    // Add more space before the total (INCREASED PADDING)
    yPosition += 10;

    pdf.setFontSize(10);
    pdf.setFont(undefined, 'bold');
    pdf.text(`Total Packs Sold: ${formatAmount(calculateTotal())}`, 20, yPosition);
    pdf.setFont(undefined, 'normal');

    yPosition += 15;

    // Add a new page for signature and certification
    if (yPosition > 180) {
      pdf.addPage();
      yPosition = 20;
    }

    // Signature Section
    pdf.setFontSize(12);
    pdf.setTextColor(0, 0, 0);
    pdf.text('Signature and Certification', 105, yPosition, { align: 'center' });
    yPosition += 15;

    // Certification Text
    pdf.setFontSize(10);
    pdf.text('I certify that I am a duly authorized owner, partner, director, officer or', 20, yPosition);
    yPosition += 6;
    pdf.text('representative of the licensee and that the statements made herein are true', 20, yPosition);
    yPosition += 6;
    pdf.text('and correct to the best of my knowledge.', 20, yPosition);
    yPosition += 15;

    // Add signature image if available
    if (signatureToUse) {
      try {
        pdf.addImage(signatureToUse, 'PNG', 20, yPosition, 80, 40);
        pdf.setFontSize(10);
        pdf.setTextColor(0, 0, 0);
        pdf.text('Signature:', 20, yPosition - 5);
        yPosition += 50;
      } catch (error) {
        console.error('Error adding signature to PDF:', error);
        pdf.setFontSize(10);
        pdf.setTextColor(0, 0, 0);
        pdf.text('Signature: Provided during submission', 20, yPosition);
        yPosition += 10;
      }
    } else {
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      pdf.text('Signature: Provided during submission', 20, yPosition);
      yPosition += 10;
    }

    // Printed Name and Title
    pdf.text(`Printed Name: ${printedName}`, 20, yPosition);
    yPosition += 6;
    pdf.text(`Title: ${title}`, 20, yPosition);
    yPosition += 8;

    // Date Signed
    pdf.text(`Date Signed: ${currentDate}`, 20, yPosition);
    yPosition += 15;

    

    return pdf;
  };

  // Function to handle PDF download from SuccessPage
  const handleDownloadPDF = () => {
    const pdf = generatePDF();
    const fileName = `NPM_Report_${distributorInfo.month}_${distributorInfo.year}_${taxpayerId || 'unknown'}.pdf`;
    pdf.save(fileName);
  };

  // Function to handle back to form
  const handleBackToForm = () => {
    setShowSuccessScreen(false);
  };

  // Submit form data to API
  const submitFormData = async (submissionData) => {
    try {
      setSubmitting(true);

      // Generate PDF with signature passed in (state update is async, so state would still be old here)
      const pdf = generatePDF(submissionData.signature);
      const pdfBlob = pdfToBlob(pdf);
      const pdfFile = new File([pdfBlob], `NPM_Report_${distributorInfo.month}_${distributorInfo.year}_${taxpayerId || 'unknown'}.pdf`, {
        type: 'application/pdf'
      });

      // Create FormData object
      const formDataToSend = new FormData();

      // Add the form fields
      formDataToSend.append('reportMonth', submissionData.reportMonth);
      formDataToSend.append('reportYear', submissionData.reportYear);

      // Add printed name and title
      formDataToSend.append('printedName', printedName);
      formDataToSend.append('title', title);

      // Add sales data as JSON string
      const salesData = {
        sales: submissionData.sales.map(sale => {
          // Find the manufacturer sval based on the selected manufacturer name
          const selectedManufacturer = npmBrands.find(brand => brand.dval === sale.manufacturerName);
          const manufacturerSval = selectedManufacturer ? selectedManufacturer.sval : null;

          return {
            packsSold: parseInt(sale.packsSold) || 0,
            brandFamily: sale.brandFamily || '',
            manufacturerName: manufacturerSval || '',
            manufacturerAddress: sale.manufacturerAddress || '',
            firstImporter: sale.firstImporter || '',
            distributorName: sale.distributorName || '',
            distributorAddress: sale.distributorAddress || '',
          }
        })
      };

      formDataToSend.append('Body', JSON.stringify(salesData));

      console.log('Sales Data:', JSON.stringify(salesData));

      // Add signature
      if (submissionData.signature) {
        const signatureFile = dataURLtoFile(submissionData.signature, 'signature.png');
        formDataToSend.append('Signature', signatureFile);
      }

      // Append PDF to FormData
      formDataToSend.append('PDF', pdfFile);

      // Construct API URL with taxpayer and reporttype
      const apiUrl = PBPUTILS + `?taxpayer=${taxpayerId}&reporttype=NPM&test=true`;

      console.log('Submitting to:', apiUrl);


      

      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formDataToSend,
      });

      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}, message: ${responseText}`);
      }

      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.warn('Response is not valid JSON, treating as plain text:', responseText);
        if (responseText.trim() === 'True') {
          return {
            success: true,
            message: 'Report submitted successfully!'
          };
        } else {
          throw new Error(`Unexpected response from server: ${responseText}`);
        }
      }

      if (result && result.Status === 'Success') {
        return {
          success: true,
          message: 'Report submitted successfully!'
        };
      } else if (result && result.Status === 'Error') {
        return {
          success: false,
          message: result.Message,
          duplicateRecord: true
        };

      } else {
        throw new Error('Unexpected response format from server');
      }

    } catch (error) {
      console.error('Error submitting form:', error);
      return {
        success: false,
        message: `Failed to submit report: ${error.message}`
      };
    } finally {
      setSubmitting(false);
    }
  };

  // Signature functions
  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
      setIsSigned(false);
    }
  };

  const handleSignatureBegin = () => {
    setIsSigned(true);
  };

  const handleSignatureEnd = () => {
    // Signature completion logic
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if signature is provided
    if (!isSigned || (sigCanvas.current && sigCanvas.current.isEmpty())) {
      alert('Please provide a signature before submitting the report.');
      return;
    }

    if (!printedName.trim()) {
      alert('Please enter your printed name.');
      return;
    }

    if (!title.trim()) {
      alert('Please enter your title.');
      return;
    }

    // Get signature data and store it for PDF generation
    const sigDataURL = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
    setSignatureDataURL(sigDataURL);

    // Prepare submission data
    const submissionData = {
      reportMonth: distributorInfo.month,
      reportYear: distributorInfo.year,
      sales: rows,
      signature: sigDataURL
    };

    console.log('Submitting data:', submissionData);

    // Store submission data for PDF generation
    setSubmissionData({
      distributorInfo,
      rows,
      taxpayerId,
      businessName,
      printedName,
      title,
      total: calculateTotal()
    });

    // Submit to API
    const result = await submitFormData(submissionData);

    if (result.success) {
      // Show success screen
      setShowSuccessScreen(true);
    } else {
      if (result.duplicateRecord) {
        alert(`${result.message}`);
      } else {
        alert(`${result.message}`);
      }
    }
  };

  // Prepare report details for SuccessPage
  const reportDetails = {
    'Report Type': 'Non-Participating Manufacturer (NPM) Sales',
    'Taxpayer ID': taxpayerId,
    'Business': businessName,
    'Period': `${distributorInfo.month} ${distributorInfo.year}`,
    'Submitted': currentDate,
  };

  // If showing success screen, render the SuccessPage component
  if (showSuccessScreen) {
    return (
      <SuccessPage
        title="NPM Sales Report Submitted Successfully!"
        subtitle={`Your Non-Participating Manufacturer sales report has been successfully submitted.`}
        downloadButtonText="Download NPM Report PDF"
        backButtonText="Back to Home"
        onDownload={handleDownloadPDF}
        onBack={handleBackToForm}
        reportDetails={reportDetails}
        contactInfo={{
          phone: "(785) 966-3060",
          email: "taxcommission@pbpnation.org",
          department: "Prairie Band Potawatomi Tax Commission"
        }}
      >
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <h3 className="font-semibold text-green-800 mb-2">Important Notes:</h3>
          <ul className="text-sm text-green-700 space-y-1">
            <li>• The report covers sales of Non-Participating Manufacturer cigarettes</li>
            <li>• Ensure all manufacturer and distributor details are accurately recorded</li>
            <li>• Contact us if you need to make any corrections</li>
          </ul>
        </div>
      </SuccessPage>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 bg-white">
      {/* Header Section */}
      <div className="text-center mb-6 sm:mb-8 border-b-2 border-[#840028] pb-4 sm:pb-6">
        <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-[#840028]">Monthly Distributor Report</h2>
        <div className="font-bold mb-2 text-[#840028] text-sm sm:text-base">
          <div>Prairie Band Potawatomi Tax Commission</div>
          <div>16281 Q Road</div>
          <div>Mayetta, KS 66509</div>
        </div>
        <div className="italic mb-2 text-[#840028] text-sm sm:text-base">
          Reports and Payment due on the 10th of each Month
        </div>
        {taxpayerId && (
          <div className="text-xs sm:text-sm text-gray-600 mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-4">
            <span>Taxpayer ID: <strong>{taxpayerId}</strong></span>
            {businessName && (
              <span>Business: <strong>{businessName}</strong></span>
            )}
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        {/* Distributor Information Section */}
        <div className="border border-gray-300 p-3 sm:p-4 mb-4 sm:mb-6">
          {/* Distributor Row - At Top */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0 mb-3">
            <label className="font-bold sm:w-48 sm:mr-4 text-[#840028] text-sm sm:text-base">Distributor:</label>
            <div className="px-3 py-2 border border-gray-300 bg-gray-50 sm:min-w-80 text-sm sm:text-base">
              {distributorInfo.distributor}
            </div>
          </div>

          {/* Month and Year Row */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-0 mb-3">
            <label className="font-bold sm:w-48 sm:mr-4 text-[#840028] text-sm sm:text-base">Month:</label>
            <select
              value={distributorInfo.month}
              onChange={(e) => handleDistributorInfoChange('month', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white sm:min-w-32 sm:mr-8 text-sm sm:text-base"
            >
              {monthOptions.map(month => (
                <option key={month} value={month}>{month}</option>
              ))}
            </select>

            <label className="font-bold sm:w-12 sm:mr-4 text-[#840028] text-sm sm:text-base">Year:</label>
            <select
              value={distributorInfo.year}
              onChange={(e) => handleDistributorInfoChange('year', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white sm:min-w-20 text-sm sm:text-base"
            >
              {yearOptions.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div className="hidden">
            <input
              type="text"
              value={distributorInfo.reportMonth}
              readOnly
            />
            <input
              type="text"
              value={distributorInfo.reportYear}
              readOnly
            />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0">
            <label className="font-bold sm:w-48 sm:mr-4 text-[#840028] text-sm sm:text-base">PBPN Tribal Distributor License Expiration Date:</label>
            <div className="px-3 py-2 border border-gray-300 bg-gray-50 sm:min-w-32 text-sm sm:text-base">
              {distributorInfo.expirationDate}
            </div>
          </div>
        </div>

        {/* Section Title */}
        <div className="bg-gray-50 px-4 py-3 mb-4 sm:mb-6 border-l-4 border-gray-500">
          <h3 className="font-semibold text-gray-800 text-sm sm:text-base">
            Sales of Non-Participating Manufacturer (NPM) Cigarettes to PBPN Retailers
          </h3>
        </div>

        {/* Table Section */}
        <div className="border border-gray-300 rounded-lg overflow-hidden mb-4 sm:mb-6 overflow-x-auto">
          <div className="min-w-[800px] sm:min-w-full">
            {/* Header Row */}
            <div className="bg-gray-50 grid grid-cols-6 gap-2 sm:gap-4 p-3 sm:p-4 border-b border-gray-300 font-semibold text-xs sm:text-sm text-gray-700">
              <div>Brand Family Name</div>
              <div># of packs</div>
              <div>Non-Participating Manufacturer (NPM)</div>
              <div>From whom Brand was purchased If Different from Manufacturer</div>
              <div>First Importer of Foreign Manufactured Brands</div>
              <div>Action</div>
            </div>

            {/* Data Rows */}
            {rows.map((row, index) => (
              <div key={row.id} className="grid grid-cols-6 gap-2 sm:gap-4 p-3 sm:p-4 bg-white border-b border-gray-200 last:border-b-0">
                {/* Brand Family Column - TEXT INPUT */}
                <div>
                  <input
                    type="text"
                    name="brandFamily"
                    value={row.brandFamily}
                    onChange={(e) => handleInputChange(e, row.id)}
                    className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="Enter brand family Name"
                  />
                </div>

                {/* Packs Column */}
                <div>
                  <input
                    type="number"
                    name="packsSold"
                    value={row.packsSold}
                    onChange={(e) => handleInputChange(e, row.id)}
                    min="0"
                    className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="0"
                  />
                </div>

                {/* Manufacturer Column - NOW WITH NPM BRANDS DROPDOWN */}
                <div className="space-y-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
                    <select
                      value={npmBrands.find(brand => brand.dval === row.manufacturerName)?.sval || ''}
                      onChange={(e) => handleManufacturerChange(row.id, e.target.value)}
                      className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option value="">Select Manufacturer</option>
                      {loadingBrands ? (
                        <option value="" disabled>Loading manufacturers...</option>
                      ) : (
                        npmBrands.map((brand) => (
                          <option key={brand.sval} value={brand.sval}>
                            {brand.dval}
                          </option>
                        ))
                      )}
                    </select>
                    {!loadingBrands && npmBrands.length === 0 && (
                      <p className="text-xs text-red-500 mt-1">No manufacturers available</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Full Name & Address</label>
                    <textarea
                      name="manufacturerAddress"
                      value={row.manufacturerAddress}
                      onChange={(e) => handleInputChange(e, row.id)}
                      rows="2"
                      className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-vertical"
                      placeholder="Manufacturer address"
                    />
                  </div>
                </div>

                {/* Distributor Column */}
                <div className="space-y-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Full Name and Address</label>
                    <select
                      value={distributors.find(dist => dist.dval === row.distributorName)?.sval || ''}
                      onChange={(e) => handleDistributorChange(row.id, e.target.value)}
                      className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option value="">Select Distributor</option>
                      {loadingDistributors ? (
                        <option value="" disabled>Loading distributors...</option>
                      ) : (
                        distributors.map((distributor) => (
                          <option key={distributor.sval} value={distributor.sval}>
                            {distributor.dval}
                          </option>
                        ))
                      )}
                    </select>
                    {!loadingDistributors && distributors.length === 0 && (
                      <p className="text-xs text-red-500 mt-1">No distributors available</p>
                    )}
                  </div>
                  <textarea
                    name="distributorAddress"
                    value={row.distributorAddress}
                    onChange={(e) => handleInputChange(e, row.id)}
                    rows="2"
                    className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-vertical"
                    placeholder="Distributor address"
                  />
                </div>

                {/* First Importer Column */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Full Name & Address</label>
                  <textarea
                    name="firstImporter"
                    value={row.firstImporter}
                    onChange={(e) => handleInputChange(e, row.id)}
                    rows="2"
                    className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-vertical"
                    placeholder="First importer details"
                  />
                </div>

                {/* Action Column */}
                <div className="flex items-start">
                  <button
                    type="button"
                    onClick={() => removeRow(row.id)}
                    disabled={rows.length === 1}
                    className="px-2 sm:px-3 py-1 bg-red-600 text-white text-xs sm:text-sm rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors w-full"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}

            {/* Total Row */}
            <div className="bg-gray-50 px-3 sm:px-4 py-3 border-t border-gray-300">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2 sm:gap-4">
                  <span className="font-semibold text-gray-700 text-sm sm:text-base">Total =</span>
                  <input
                    type="number"
                    value={calculateTotal()}
                    className="w-24 sm:w-32 px-2 sm:px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 font-semibold text-sm"
                    min="0"
                    readOnly
                  />
                </div>
                <button
                  type="button"
                  onClick={addRow}
                  className="px-3 sm:px-4 py-2 bg-green-600 text-white font-medium rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors text-sm w-full sm:w-auto"
                >
                  Add Row
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Signature Section */}
        <div className="border border-gray-300 p-4 sm:p-6 mb-4 sm:mb-6 rounded-lg">
          <h3 className="text-lg font-bold mb-3 sm:mb-4 text-center text-gray-800">Signature</h3>

          <div className="mb-4">
            <label className="font-bold block mb-2 text-gray-700 text-sm sm:text-base">
              Sign below to certify the accuracy of this report:
            </label>
            <div
              id="signature-container"
              className="border-2 border-gray-300 rounded-lg p-3 sm:p-4 bg-white"
            >
              {canvasSize.width > 0 && (
                <SignatureCanvas
                  ref={sigCanvas}
                  penColor="#0000FF"
                  backgroundColor="rgb(255, 255, 255)"
                  canvasProps={{
                    width: canvasSize.width,
                    height: canvasSize.height,
                    className: 'signature-canvas w-full border border-gray-400 rounded bg-white cursor-crosshair'
                  }}
                  onBegin={handleSignatureBegin}
                  onEnd={handleSignatureEnd}
                  clearOnResize={false}
                />
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
            <div className="text-sm text-gray-600 text-center sm:text-left">
              {isSigned ? (
                <span className="text-green-600 font-bold">✓ Signature provided</span>
              ) : (
                <span className="text-red-600">Please sign above</span>
              )}
            </div>

            <button
              type="button"
              onClick={clearSignature}
              className="px-4 py-2 bg-[#840028] text-white rounded hover:bg-[#6a0020] transition duration-200 text-sm w-full sm:w-auto"
            >
              Clear Signature
            </button>
          </div>

          {/* Printed Name and Title Fields */}
          <div className="border-t pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Printed Name: <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={printedName}
                  onChange={(e) => setPrintedName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="Enter your full name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title: <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="Enter your title"
                  required
                />
              </div>
            </div>

            {/* Date Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date Signed:
              </label>
              <input
                type="text"
                value={currentDate}
                readOnly
                className="w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 font-medium text-gray-700 cursor-not-allowed text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                This date is automatically set to today's date and cannot be modified
              </p>
            </div>
          </div>
        </div>

        {/* Certification */}
        <div className="border-t-2 border-b-2 border-[#840028] py-4 sm:py-6 my-6 sm:my-8 text-center italic text-[#840028] text-sm sm:text-base">
          <p>
            I certify that I am a duly authorized owner, partner, director, officer or representative of the licensee and that the statements made herein are true and correct to the best of my knowledge.
          </p>
        </div>

        {/* Form Actions */}
        <div className="flex justify-center mt-6 sm:mt-8">
          <button
            type="submit"
            disabled={!isSigned || submitting || !printedName.trim() || !title.trim()}
            className="bg-[#840028] hover:bg-[#6a0020] text-white font-bold py-3 px-6 sm:px-8 rounded-lg transition duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed w-full sm:w-auto text-sm sm:text-base"
          >
            {submitting ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Submitting...
              </span>
            ) : (
              isSigned && printedName.trim() && title.trim() ? 'Submit Report' : 'Please Complete All Required Fields'
            )}
          </button>
        </div>
      </form>

      {/* Custom CSS for signature canvas */}
      <style jsx>{`
        .signature-canvas {
          cursor: crosshair !important;
          touch-action: none;
        }
        .signature-canvas:active {
          cursor: grabbing !important;
        }
        #signature-container {
          min-height: 200px;
        }
        
        @media (max-width: 640px) {
          #signature-container {
            min-height: 150px;
          }
        }
      `}</style>
    </div>
  );
};

export default DistributorReportForm;